import { createContext, useContext, type ReactNode } from 'react';

export interface DialogDrawerRegistryValue {
  /** Register a DialogDrawer instance; returns its id (empty string when no provider). */
  register: () => string;
  /** Remove a previously-registered instance. */
  unregister: (id: string) => void;
  /** Report the open state for a registered instance. */
  trackOpen: (id: string, open: boolean) => void;
}

const noop = () => {};

const defaultRegistry: DialogDrawerRegistryValue = {
  register: () => '',
  unregister: noop,
  trackOpen: noop,
};

/**
 * Injection point for app-level open-state coordination of `DialogDrawer`.
 *
 * Defaults to a no-op registry so the design-system primitive stays
 * side-effect-free with no state library wired up (Storybook, standalone
 * consumers, tests). The host app mounts a provider backed by its global
 * store to keep dialogs and dropdowns from being open simultaneously.
 */
export const DialogDrawerRegistryContext =
  createContext<DialogDrawerRegistryValue>(defaultRegistry);

export function DialogDrawerRegistryContextProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: DialogDrawerRegistryValue;
}) {
  return (
    <DialogDrawerRegistryContext.Provider value={value}>
      {children}
    </DialogDrawerRegistryContext.Provider>
  );
}

export function useDialogDrawerRegistry(): DialogDrawerRegistryValue {
  return useContext(DialogDrawerRegistryContext);
}
